import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Music, Mic2, Radio, Camera, Headphones, Building2, Headphones as ListenerIcon, ChevronRight, Check, Loader2 } from 'lucide-react';
import { API } from '../config/api';



const ROLE_OPTIONS = [
  {
    id: 'listener',
    title: 'Listener',
    description: 'Discover and support DMV artists. Stream music, follow favorites, attend events.',
    icon: ListenerIcon,
    color: 'gray',
    isDefault: true,
    requirements: null
  },
  {
    id: 'artist',
    title: 'Artist',
    description: 'Release music, build your fanbase, chart on DMV Soundstage, and get discovered.',
    icon: Music,
    color: 'secondary',
    requirements: 'Requires DSP links (Spotify, Apple Music)',
    tierRequired: true
  },
  {
    id: 'podcaster',
    title: 'Podcaster',
    description: 'Host your podcast, interview DMV artists, build your audience.',
    icon: Mic2,
    color: 'green',
    requirements: 'Podcast name, cover art, and at least one platform link'
  },
  {
    id: 'streamer',
    title: 'Streamer / Host',
    description: 'Host live sessions, listening parties, interviews, and panels.',
    icon: Radio,
    color: 'red',
    requirements: 'Display name and streaming platform'
  },
  {
    id: 'studio',
    title: 'Recording Studio',
    description: 'List your studio for booking by DMV artists and creatives.',
    icon: Building2,
    color: 'amber',
    requirements: 'Studio name, address, services, and booking info'
  },
  {
    id: 'videographer',
    title: 'Videographer',
    description: 'Shoot music videos, interviews, and visual content for the DMV scene.',
    icon: Camera,
    color: 'purple',
    requirements: 'Portfolio and services offered'
  },
  {
    id: 'engineer',
    title: 'Audio Engineer',
    description: 'Mix, master, and engineer tracks for DMV artists.',
    icon: Headphones,
    color: 'blue',
    requirements: 'Specialties and DAW proficiency'
  }
];

export default function RoleSelectionPage() {
  const { user, getAuthHeaders, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [selectedRoles, setSelectedRoles] = useState(['listener']);
  const [submitting, setSubmitting] = useState(false);

  const toggleRole = (roleId) => {
    if (roleId === 'listener') return; // Listener is always selected
    
    setSelectedRoles(prev => {
      if (prev.includes(roleId)) {
        return prev.filter(r => r !== roleId);
      } else {
        return [...prev, roleId];
      }
    });
  };

  const handleContinue = async () => {
    // If only listener selected, go to home
    if (selectedRoles.length === 1 && selectedRoles[0] === 'listener') {
      navigate('/home');
      return;
    }

    // Navigate to role setup page for the first selected non-listener role
    const primaryRole = selectedRoles.find(r => r !== 'listener');
    
    if (primaryRole) {
      navigate(`/setup-role?role=${primaryRole}`);
    } else {
      navigate('/home');
    }
  };

  const getColorClasses = (color, isSelected) => {
    const colors = {
      gray: isSelected ? 'border-gray-400 bg-gray-500/10' : 'border-border hover:border-border',
      secondary: isSelected ? 'border-secondary bg-secondary/10' : 'border-border hover:border-secondary/30',
      green: isSelected ? 'border-green-500 bg-green-500/10' : 'border-border hover:border-green-500/30',
      red: isSelected ? 'border-red-500 bg-red-500/10' : 'border-border hover:border-red-500/30',
      amber: isSelected ? 'border-amber-500 bg-amber-500/10' : 'border-border hover:border-amber-500/30',
      purple: isSelected ? 'border-purple-500 bg-purple-500/10' : 'border-border hover:border-purple-500/30',
      blue: isSelected ? 'border-blue-500 bg-blue-500/10' : 'border-border hover:border-blue-500/30'
    };
    return colors[color] || colors.gray;
  };

  const getIconColorClass = (color) => {
    const colors = {
      gray: 'text-gray-400',
      secondary: 'text-secondary',
      green: 'text-green-400',
      red: 'text-red-400',
      amber: 'text-amber-400',
      purple: 'text-purple-400',
      blue: 'text-blue-400'
    };
    return colors[color] || 'text-gray-400';
  };

  return (
    <div className="min-h-screen py-12 px-4" data-testid="role-selection-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold uppercase mb-3">
            What would you like to do on <span className="text-secondary">DMV Soundstage</span>?
          </h1>
          <p className="text-muted-foreground text-lg">
            Select all that apply. You can always add more roles later.
          </p>
        </div>

        {/* Role Cards Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {ROLE_OPTIONS.map((role) => {
            const Icon = role.icon;
            const isSelected = selectedRoles.includes(role.id);
            
            return (
              <Card
                key={role.id}
                className={`glass-card p-4 cursor-pointer transition-all ${getColorClasses(role.color, isSelected)} ${role.isDefault ? 'opacity-60' : ''}`}
                onClick={() => toggleRole(role.id)}
                data-testid={`role-card-${role.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className={`w-10 h-10 rounded-lg bg-muted/50 flex items-center justify-center`}>
                    <Icon className={`w-5 h-5 ${getIconColorClass(role.color)}`} />
                  </div>
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                    isSelected 
                      ? `border-${role.color === 'secondary' ? 'secondary' : role.color + '-500'} bg-${role.color === 'secondary' ? 'secondary' : role.color + '-500'}` 
                      : 'border-border'
                  }`}>
                    {isSelected && <Check className="w-4 h-4 text-white" />}
                  </div>
                </div>
                <h3 className="font-bold mb-1">{role.title}</h3>
                <p className="text-sm text-muted-foreground mb-2">{role.description}</p>
                {role.requirements && (
                  <p className="text-xs text-muted-foreground/70 italic">{role.requirements}</p>
                )}
                {role.tierRequired && (
                  <p className="text-xs text-secondary mt-1">Subscription required</p>
                )}
              </Card>
            );
          })}
        </div>

        {/* Continue Button */}
        <div className="flex flex-col items-center gap-4">
          <Button
            onClick={handleContinue}
            disabled={submitting}
            size="lg"
            className="px-8"
            data-testid="continue-btn"
          >
            {submitting ? (
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            ) : (
              <ChevronRight className="w-5 h-5 mr-2" />
            )}
            {selectedRoles.length === 1 ? 'Continue as Listener' : 'Set Up Selected Roles'}
          </Button>
          
          <p className="text-xs text-muted-foreground text-center max-w-md">
            One account, multiple roles. Your DMV Soundstage account can hold Artist, Podcaster, Studio, and more — all in one place.
          </p>
        </div>
      </div>
    </div>
  );
}
